import React, { useState } from "react";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  AlertTitle,
  Box,
  Divider,
  FormControlLabel,
  Grid,
  Tab,
  Switch,
  Typography,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { useTranslation } from "react-i18next";
import Div from "@jumbo/shared/Div/Div";
import { Label } from "app/pages/presentation/module/form/types";
import { Field, useFormikContext } from "formik";
import * as Yup from "yup";
import { TabContext, TabList, TabPanel } from "@mui/lab";

export const DSOK_UI_SETTINGS_FIELD_NAME = "DSOK_UI_SETTINGS_FIELD";

export const getDsokCustomerFieldValidationSchema = (t) => ({
  storeCode: Yup.string().required(t("messages.checkRequiredFields")),
  customerId: Yup.number().required(t("messages.checkRequiredFields")),
  code: Yup.string().required(t("messages.checkRequiredFields")),
  languages: Yup.array()
    .min(1, t("messages.checkRequiredFields"))
    .required(t("messages.checkRequiredFields")),
  defaultLanguage: Yup.string().required(t("messages.checkRequiredFields")),
});

const SmartkioskUISettingsFields = ({
  handleChange,
  expanded,
  hideSectionTitle = false,
}) => {
  const { t } = useTranslation();
  const { values, setFieldValue } = useFormikContext();
  const [activeTab, setActiveTab] = useState("general");

  const renderInfoList = (items) => (
    <Box component="ul" sx={{ m: 0, pl: 2.5, "& li": { mb: 0.5 } }}>
      {items.map(({ key, labelKey }) => (
        <li key={key}>
          <Typography variant="body2" component="span">
            <strong>{t(labelKey)}:</strong> {t(`pages.dsokDetails.customerUISettings.info.${key}`)}
          </Typography>
        </li>
      ))}
    </Box>
  );

  const content = (
    <Grid container spacing={2}>
          <Grid item xs={12} md={12}>
            <TabContext value={activeTab}>
              <Div sx={{ borderBottom: 1, borderColor: "divider" }}>
                <TabList onChange={(_, newTab) => setActiveTab(newTab)}>
                  <Tab
                    label={t(
                      "pages.dsokDetails.customerUISettings.tabs.general"
                    )}
                    value={"general"}
                  />
                  <Tab
                    label={t(
                      "pages.dsokDetails.customerUISettings.tabs.orders"
                    )}
                    value={"orders"}
                  />
                  <Tab
                    label={t(
                      "pages.dsokDetails.customerUISettings.tabs.products"
                    )}
                    value={"products"}
                  />
                  <Tab
                    label={t(
                      "pages.dsokDetails.customerUISettings.tabs.aiSettings"
                    )}
                    value={"aiSettings"}
                  />
                </TabList>
              </Div>

              <TabPanel value={"general"} sx={{ p: 0, m: 0 }}>
                <Grid
                  item
                  xs={12}
                  md={12}
                  lg={12}
                  container
                  spacing={2}
                  sx={{ p: 0, m: 0 }}
                >
                  <Grid item xs={12} sx={{ mb: 2 }}>
                    <Alert severity="info" variant="outlined">
                      <AlertTitle>{t("pages.dsokDetails.customerUISettings.info.title")}</AlertTitle>
                      <Typography variant="body2" component="span" sx={{ display: "block", mb: 1 }}>
                        {t("pages.dsokDetails.customerUISettings.info.intro")}
                      </Typography>
                      {renderInfoList([
                        { key: "enableProducts", labelKey: "pages.dsokDetails.customerUISettings.enableProducts" },
                        { key: "enableSpecialOffers", labelKey: "pages.dsokDetails.customerUISettings.enableSpecialOffers" },
                        { key: "enableProductAttributes", labelKey: "pages.dsokDetails.customerUISettings.enableProductAttributes" },
                        { key: "enableCategories", labelKey: "pages.dsokDetails.customerUISettings.enableCategories" },
                        { key: "enableDailyMenu", labelKey: "pages.dsokDetails.customerUISettings.enableDailyMenu" },
                        { key: "enableMenuManager", labelKey: "pages.dsokDetails.customerUISettings.enableMenuManager" },
                        { key: "enableClients", labelKey: "pages.dsokDetails.customerUISettings.enableClients" },
                        { key: "enableReports", labelKey: "pages.dsokDetails.customerUISettings.enableReports" },
                        { key: "enableSettings", labelKey: "pages.dsokDetails.customerUISettings.enableSettings" },
                      ])}
                    </Alert>
                  </Grid>
                  {/* Sekcia 1: Produkty a kategórie */}
                  <Grid item xs={12} sx={{ mt: 2 }}>
                    <Typography variant="h6" component="h2">
                      {t(
                        "pages.dsokDetails.customerUISettings.sections.productsAndCategories",
                        {
                          defaultValue: "Produkty a kategórie",
                        }
                      )}
                    </Typography>
                    <Divider sx={{ mt: 1, mb: 2 }} />
                  </Grid>
                  <Grid item xs={12} md={4} lg={3}>
                    <Div sx={{ mt: 1, mb: 1, mx: 0.5 }}>
                      <FormControlLabel
                        control={
                          <Field
                            name="customer.customerUISettings.enableProducts"
                            component={Switch}
                            onChange={(e) =>
                              setFieldValue(
                                "customer.customerUISettings.enableProducts",
                                e.target.checked
                              )
                            }
                          />
                        }
                        label={t(
                          "pages.dsokDetails.customerUISettings.enableProducts"
                        )}
                        checked={
                          values.customer?.customerUISettings?.enableProducts
                        }
                      />
                    </Div>
                  </Grid>
                  <Grid item xs={12} md={4} lg={3}>
                    <Div sx={{ mt: 1, mb: 1, mx: 0.5 }}>
                      <FormControlLabel
                        control={
                          <Field
                            name="customer.customerUISettings.enableSpecialOffers"
                            component={Switch}
                            onChange={(e) =>
                              setFieldValue(
                                "customer.customerUISettings.enableSpecialOffers",
                                e.target.checked
                              )
                            }
                          />
                        }
                        label={
                          <Label
                            text={t(
                              "pages.dsokDetails.customerUISettings.enableSpecialOffers"
                            )}
                            helpText={t(
                              "pages.dsokDetails.customerUISettings.enableSpecialOffersHelp"
                            )}
                            sx={{ color: "text.primary" }}
                          />
                        }
                        checked={
                          values.customer?.customerUISettings?.enableSpecialOffers === true
                        }
                      />
                    </Div>
                  </Grid>
                  <Grid item xs={12} md={4} lg={3}>
                    <Div sx={{ mt: 1, mb: 1, mx: 0.5 }}>
                      <FormControlLabel
                        control={
                          <Field
                            name="customer.customerUISettings.enableProductAttributes"
                            component={Switch}
                            onChange={(e) =>
                              setFieldValue(
                                "customer.customerUISettings.enableProductAttributes",
                                e.target.checked
                              )
                            }
                          />
                        }
                        label={t(
                          "pages.dsokDetails.customerUISettings.enableProductAttributes"
                        )}
                        checked={
                          values.customer?.customerUISettings
                            ?.enableProductAttributes
                        }
                      />
                    </Div>
                  </Grid>
                  <Grid item xs={12} md={4} lg={3}>
                    <Div sx={{ mt: 1, mb: 1, mx: 0.5 }}>
                      <FormControlLabel
                        control={
                          <Field
                            name="customer.customerUISettings.enableCategories"
                            component={Switch}
                            onChange={(e) =>
                              setFieldValue(
                                "customer.customerUISettings.enableCategories",
                                e.target.checked
                              )
                            }
                          />
                        }
                        label={t(
                          "pages.dsokDetails.customerUISettings.enableCategories"
                        )}
                        checked={
                          values.customer?.customerUISettings?.enableCategories
                        }
                      />
                    </Div>
                  </Grid>

                  {/* Sekcia 2: Digital Menu Board */}
                  <Grid item xs={12} sx={{ mt: 2 }}>
                    <Typography variant="h6" component="h2">
                      {t(
                        "pages.dsokDetails.customerUISettings.sections.digitalMenuBoard",
                        {
                          defaultValue: "Digital Menu Board",
                        }
                      )}
                    </Typography>
                    <Divider sx={{ mt: 1, mb: 2 }} />
                  </Grid>
                  <Grid item xs={12} md={4} lg={3}>
                    <Div sx={{ mt: 1, mb: 1, mx: 0.5 }}>
                      <FormControlLabel
                        control={
                          <Field
                            name="customer.customerUISettings.enableDailyMenu"
                            component={Switch}
                            onChange={(e) =>
                              setFieldValue(
                                "customer.customerUISettings.enableDailyMenu",
                                e.target.checked
                              )
                            }
                          />
                        }
                        label={t(
                          "pages.dsokDetails.customerUISettings.enableDailyMenu",
                          { defaultValue: "Denné menu" }
                        )}
                        checked={
                          values.customer?.customerUISettings
                            ?.enableDailyMenu === true
                        }
                      />
                    </Div>
                  </Grid>
                  <Grid item xs={12} md={4} lg={3}>
                    <Div sx={{ mt: 1, mb: 1, mx: 0.5 }}>
                      <FormControlLabel
                        control={
                          <Field
                            name="customer.customerUISettings.enableMenuManager"
                            component={Switch}
                            onChange={(e) =>
                              setFieldValue(
                                "customer.customerUISettings.enableMenuManager",
                                e.target.checked
                              )
                            }
                          />
                        }
                        label={t(
                          "pages.dsokDetails.customerUISettings.enableMenuManager",
                          { defaultValue: "Menu Manager" }
                        )}
                        checked={
                          values.customer?.customerUISettings
                            ?.enableMenuManager === true
                        }
                      />
                    </Div>
                  </Grid>
                  <Grid item xs={12} md={4} lg={3}>
                    <Div sx={{ mt: 1, mb: 1, mx: 0.5 }}>
                      <FormControlLabel
                        control={
                          <Field
                            name="customer.customerUISettings.enableDailyMenuWebExport"
                            component={Switch}
                            onChange={(e) =>
                              setFieldValue(
                                "customer.customerUISettings.enableDailyMenuWebExport",
                                e.target.checked
                              )
                            }
                          />
                        }
                        label={t(
                          "pages.dsokDetails.customerUISettings.enableDailyMenuWebExport",
                          { defaultValue: "Export Menu na WEB" }
                        )}
                        checked={
                          values.customer?.customerUISettings
                            ?.enableDailyMenuWebExport === true
                        }
                      />
                    </Div>
                  </Grid>
                  <Grid item xs={12} md={4} lg={3}>
                    <Div sx={{ mt: 1, mb: 1, mx: 0.5 }}>
                      <FormControlLabel
                        control={
                          <Field
                            name="customer.customerUISettings.enableDailyMenuEmail"
                            component={Switch}
                            onChange={(e) =>
                              setFieldValue(
                                "customer.customerUISettings.enableDailyMenuEmail",
                                e.target.checked
                              )
                            }
                          />
                        }
                        label={t(
                          "pages.dsokDetails.customerUISettings.enableDailyMenuEmail",
                          { defaultValue: "Posielať menu emailom" }
                        )}
                        checked={
                          values.customer?.customerUISettings
                            ?.enableDailyMenuEmail === true
                        }
                      />
                    </Div>
                  </Grid>

                  {/* Sekcia 3: Ostatné */}
                  <Grid item xs={12} sx={{ mt: 2 }}>
                    <Typography variant="h6" component="h2">
                      {t(
                        "pages.dsokDetails.customerUISettings.sections.other",
                        {
                          defaultValue: "Ostatné",
                        }
                      )}
                    </Typography>
                    <Divider sx={{ mt: 1, mb: 2 }} />
                  </Grid>
                  <Grid item xs={12} md={4} lg={3}>
                    <Div sx={{ mt: 1, mb: 1, mx: 0.5 }}>
                      <FormControlLabel
                        control={
                          <Field
                            name="customer.customerUISettings.enableClients"
                            component={Switch}
                            onChange={(e) =>
                              setFieldValue(
                                "customer.customerUISettings.enableClients",
                                e.target.checked
                              )
                            }
                          />
                        }
                        label={t(
                          "pages.dsokDetails.customerUISettings.enableClients"
                        )}
                        checked={
                          values.customer?.customerUISettings?.enableClients
                        }
                      />
                    </Div>
                  </Grid>
                  <Grid item xs={12} md={4} lg={3}>
                    <Div sx={{ mt: 1, mb: 1, mx: 0.5 }}>
                      <FormControlLabel
                        control={
                          <Field
                            name="customer.customerUISettings.enableReports"
                            component={Switch}
                            onChange={(e) =>
                              setFieldValue(
                                "customer.customerUISettings.enableReports",
                                e.target.checked
                              )
                            }
                          />
                        }
                        label={t(
                          "pages.dsokDetails.customerUISettings.enableReports"
                        )}
                        checked={
                          values.customer?.customerUISettings?.enableReports
                        }
                      />
                    </Div>
                  </Grid>
                  <Grid item xs={12} md={4} lg={3}>
                    <Div sx={{ mt: 1, mb: 1, mx: 0.5 }}>
                      <FormControlLabel
                        control={
                          <Field
                            name="customer.customerUISettings.enableSettings"
                            component={Switch}
                            onChange={(e) =>
                              setFieldValue(
                                "customer.customerUISettings.enableSettings",
                                e.target.checked
                              )
                            }
                          />
                        }
                        label={t(
                          "pages.dsokDetails.customerUISettings.enableSettings"
                        )}
                        checked={
                          values.customer?.customerUISettings?.enableSettings
                        }
                      />
                    </Div>
                  </Grid>
                </Grid>
              </TabPanel>
              <TabPanel value={"orders"} sx={{ p: 0, m: 0 }}>
                <Grid
                  item
                  xs={12}
                  md={12}
                  lg={12}
                  container
                  spacing={2}
                  sx={{ p: 0, m: 0 }}
                >
                  <Grid item xs={12} sx={{ mb: 2 }}>
                    <Alert variant="outlined" severity="info">
                      <AlertTitle>{t("pages.dsokDetails.customerUISettings.info.title")}</AlertTitle>
                      <Typography variant="body2" component="span" sx={{ display: "block", mb: 1 }}>
                        {t("pages.dsokDetails.customerUISettings.orderHelpText")}
                      </Typography>
                      {renderInfoList([
                        { key: "enablePayment", labelKey: "pages.dsokDetails.customerUISettings.enablePayment" },
                        { key: "enablePrintOrEkasaStatus", labelKey: "pages.dsokDetails.customerUISettings.enablePrintOrEkasaStatus" },
                        { key: "enableClientsInList", labelKey: "pages.dsokDetails.customerUISettings.enableClientsInList" },
                      ])}
                    </Alert>
                  </Grid>
                  <Grid item xs={12} md={4} lg={4}>
                    <Div sx={{ mt: 1, mb: 1, mx: 0.5 }}>
                      <FormControlLabel
                        control={
                          <Field
                            name="customer.customerUISettings.enablePayment"
                            component={Switch}
                            onChange={(e) =>
                              setFieldValue(
                                "customer.customerUISettings.enablePayment",
                                e.target.checked
                              )
                            }
                          />
                        }
                        label={t(
                          "pages.dsokDetails.customerUISettings.enablePayment"
                        )}
                        checked={
                          values.customer?.customerUISettings?.enablePayment
                        }
                      />
                    </Div>
                  </Grid>
                  <Grid item xs={12} md={4} lg={4}>
                    <Div sx={{ mt: 1, mb: 1, mx: 0.5 }}>
                      <FormControlLabel
                        control={
                          <Field
                            name="customer.customerUISettings.enablePrintOrEkasaStatus"
                            component={Switch}
                            onChange={(e) =>
                              setFieldValue(
                                "customer.customerUISettings.enablePrintOrEkasaStatus",
                                e.target.checked
                              )
                            }
                          />
                        }
                        label={t(
                          "pages.dsokDetails.customerUISettings.enablePrintOrEkasaStatus"
                        )}
                        checked={
                          values.customer?.customerUISettings
                            ?.enablePrintOrEkasaStatus
                        }
                      />
                    </Div>
                  </Grid>
                  <Grid item xs={12} md={4} lg={4}>
                    <Div sx={{ mt: 1, mb: 1, mx: 0.5 }}>
                      <FormControlLabel
                        control={
                          <Field
                            name="customer.customerUISettings.enableClientsInList"
                            component={Switch}
                            onChange={(e) =>
                              setFieldValue(
                                "customer.customerUISettings.enableClientsInList",
                                e.target.checked
                              )
                            }
                          />
                        }
                        label={t(
                          "pages.dsokDetails.customerUISettings.enableClientsInList"
                        )}
                        checked={
                          values.customer?.customerUISettings
                            ?.enableClientsInList
                        }
                      />
                    </Div>
                  </Grid>
                  <Grid item xs={12} md={4} lg={4}>
                    <Div sx={{ mt: 1, mb: 1, mx: 0.5 }}>
                      <FormControlLabel
                        control={
                          <Field
                            name="customer.customerUISettings.autoAddBindingsToOrder"
                            component={Switch}
                            onChange={(e) =>
                              setFieldValue(
                                "customer.customerUISettings.autoAddBindingsToOrder",
                                e.target.checked
                              )
                            }
                          />
                        }
                        label={t(
                          "pages.dsokDetails.customerUISettings.autoAddBindingsToOrder",
                          { defaultValue: "Auto-add bindings to order (dočasné)" }
                        )}
                        checked={
                          values.customer?.customerUISettings
                            ?.autoAddBindingsToOrder === true
                        }
                      />
                    </Div>
                  </Grid>
                </Grid>
              </TabPanel>
              <TabPanel value={"products"} sx={{ p: 0, m: 0 }}>
                <Grid
                  item
                  xs={12}
                  md={12}
                  lg={12}
                  container
                  spacing={2}
                  sx={{ p: 0, m: 0 }}
                >
                  <Grid item xs={12} sx={{ mb: 2 }}>
                    <Alert severity="info" variant="outlined">
                      <AlertTitle>{t("pages.dsokDetails.customerUISettings.info.title")}</AlertTitle>
                      <Typography variant="body2" component="span" sx={{ display: "block", mb: 1 }}>
                        {t("pages.dsokDetails.customerUISettings.info.intro")}
                      </Typography>
                      {renderInfoList([
                        { key: "enableStocks", labelKey: "pages.dsokDetails.customerUISettings.enableStocks" },
                        { key: "enableBindings", labelKey: "pages.dsokDetails.customerUISettings.enableBindings" },
                        { key: "enablePrinters", labelKey: "pages.dsokDetails.customerUISettings.enablePrinters" },
                      ])}
                    </Alert>
                  </Grid>
                  <Grid item xs={12} md={4} lg={3}>
                    <Div sx={{ mt: 1, mb: 1, mx: 0.5 }}>
                      <FormControlLabel
                        control={
                          <Field
                            name="customer.customerUISettings.enableStocks"
                            component={Switch}
                            onChange={(e) =>
                              setFieldValue(
                                "customer.customerUISettings.enableStocks",
                                e.target.checked
                              )
                            }
                          />
                        }
                        label={t(
                          "pages.dsokDetails.customerUISettings.enableStocks"
                        )}
                        checked={
                          values.customer?.customerUISettings?.enableStocks
                        }
                      />
                    </Div>
                  </Grid>
                  <Grid item xs={12} md={4} lg={3}>
                    <Div sx={{ mt: 1, mb: 1, mx: 0.5 }}>
                      <FormControlLabel
                        control={
                          <Field
                            name="customer.customerUISettings.enableBindings"
                            component={Switch}
                            onChange={(e) =>
                              setFieldValue(
                                "customer.customerUISettings.enableBindings",
                                e.target.checked
                              )
                            }
                          />
                        }
                        label={t(
                          "pages.dsokDetails.customerUISettings.enableBindings"
                        )}
                        checked={
                          values.customer?.customerUISettings?.enableBindings
                        }
                      />
                    </Div>
                  </Grid>
                  <Grid item xs={12} md={4} lg={3}>
                    <Div sx={{ mt: 1, mb: 1, mx: 0.5 }}>
                      <FormControlLabel
                        control={
                          <Field
                            name="customer.customerUISettings.enablePrinters"
                            component={Switch}
                            onChange={(e) =>
                              setFieldValue(
                                "customer.customerUISettings.enablePrinters",
                                e.target.checked
                              )
                            }
                          />
                        }
                        label={
                          <Label
                            text={t(
                              "pages.dsokDetails.customerUISettings.enablePrinters"
                            )}
                            helpText={t(
                              "pages.dsokDetails.customerUISettings.enablePrintersHelp"
                            )}
                            sx={{ color: "text.primary" }}
                          />
                        }
                        checked={
                          values.customer?.customerUISettings?.enablePrinters === true
                        }
                      />
                    </Div>
                  </Grid>
                </Grid>
              </TabPanel>
              <TabPanel value={"aiSettings"} sx={{ p: 0, m: 0 }}>
                <Grid item xs={12} md={12} lg={12} container spacing={2} sx={{ p: 0, m: 0 }}>
                  <Grid item xs={12} sx={{ mb: 2 }}>
                    <Alert severity="info" variant="outlined">
                      <AlertTitle>{t("pages.dsokDetails.customerUISettings.info.title")}</AlertTitle>
                      <Typography variant="body2" component="span" sx={{ display: "block", mb: 1 }}>
                        {t("pages.dsokDetails.customerUISettings.info.intro")}
                      </Typography>
                      {renderInfoList([
                        { key: "enableAiCategoryImageGeneration", labelKey: "pages.dsokDetails.customerUISettings.enableAiCategoryImageGeneration" },
                      ])}
                    </Alert>
                  </Grid>
                  <Grid item xs={12} md={4} lg={3}>
                    <Div sx={{ mt: 1, mb: 1, mx: 0.5 }}>
                      <FormControlLabel
                        control={
                          <Field
                            name="customer.customerUISettings.enableAiCategoryImageGeneration"
                            component={Switch}
                            onChange={(e) =>
                              setFieldValue(
                                "customer.customerUISettings.enableAiCategoryImageGeneration",
                                e.target.checked
                              )
                            }
                          />
                        }
                        label={t(
                          "pages.dsokDetails.customerUISettings.enableAiCategoryImageGeneration"
                        )}
                        checked={
                          values.customer?.customerUISettings
                            ?.enableAiCategoryImageGeneration
                        }
                      />
                    </Div>
                  </Grid>
                </Grid>
              </TabPanel>
            </TabContext>
          </Grid>
        </Grid>
  );

  if (hideSectionTitle) {
    return content;
  }

  return (
    <Accordion
      expanded={expanded === DSOK_UI_SETTINGS_FIELD_NAME}
      onChange={handleChange(DSOK_UI_SETTINGS_FIELD_NAME)}
    >
      <AccordionSummary expandIcon={<ExpandMoreIcon />}>
        <Typography sx={{ width: "33%", flexShrink: 0 }}>
          {t("pages.dsokDetails.customerUISettings.title")}
        </Typography>
      </AccordionSummary>
      <AccordionDetails>{content}</AccordionDetails>
    </Accordion>
  );
};

export default SmartkioskUISettingsFields;
